import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useInView } from "react-intersection-observer";

let imagesCache = {};
let sizesCache = {};

const STATUS_LAZY = "lazy";
const STATUS_LOADING = "loading";
const STATUS_ERROR = "error";
const STATUS_DONE = "done";

const resetImageCache = () => {
  imagesCache = {};
  sizesCache = {};
};

const getCacheKey = (images) => {
  const originalSrc = images[STATUS_DONE].src;

  try {
    const FAKE_HOST_FOR_RELATIVE_URLS = "example.com";
    const url = new URL(originalSrc, `https://${FAKE_HOST_FOR_RELATIVE_URLS}`);
    return url.hostname === FAKE_HOST_FOR_RELATIVE_URLS
      ? url.pathname + url.search
      : url.toString();
  } catch (error) {
    return originalSrc;
  }
};

const getInitialState = (images, preset, dangerouslyDisableLazyLoad) => {
  return {
    status:
      dangerouslyDisableLazyLoad || imagesCache[getCacheKey(images)]
        ? STATUS_DONE
        : STATUS_LAZY,
    images: images,
    size: sizesCache[getCacheKey(images)]
      ? sizesCache[getCacheKey(images)]
      : preset
      ? {
          width: preset.width,
          height: preset.height || preset.placeholderHeight,
        }
      : undefined,
  };
};

const useResetStateForNewImagesProps = (
  images,
  dangerouslyDisableLazyLoad,
  initialState,
  setStatus
) => {
  useEffect(() => {
    setStatus((state) => {
      if (
        getCacheKey(images) === getCacheKey(state.images) &&
        !dangerouslyDisableLazyLoad
      ) {
        return state;
      }

      return initialState;
    });
  }, [images, dangerouslyDisableLazyLoad, initialState, setStatus]);
};

const listenImageLoad = (imageElement, onLoad, onError) => {
  imageElement.addEventListener("load", onLoad);
  imageElement.addEventListener("error", onError);

  return () => {
    imageElement.removeEventListener("load", onLoad);
    imageElement.removeEventListener("error", onError);
  };
};

const onLoad = (images, setStatus) => (event) => {
  const size = {
    width: event.currentTarget.naturalWidth,
    height: event.currentTarget.naturalHeight,
  };
  imagesCache[getCacheKey(images)] = true;
  sizesCache[getCacheKey(images)] = size;
  setStatus((state) => ({
    ...state,
    status: STATUS_DONE,
    size: size,
  }));
};

const onError = (setStatus) => (event) => {
  setStatus((state) => ({ ...state, status: STATUS_ERROR }));
};

const useImageLoad = (
  images,
  dangerouslyDisableLazyLoad,
  ref,
  status,
  setStatus
) => {
  useEffect(() => {
    if (status !== STATUS_LOADING || !ref.current) {
      return;
    }

    const imageElement = ref.current.querySelector("img");
    if (process.env.NODE_ENV === "development") {
      // This allows early detecting if images are not working correctly
      // correctly, but allows images to still display in production.
      const currentSrc = imageElement?.src?.replace(window.location.origin, "");
      if (
        currentSrc !== getCacheKey(images).replace(window.location.origin, "")
      ) {
        console.warn(`
        Your image component does not display the image with the correct properties. Please make sure that you have an <img> tag with the src and srcset coming from the \`images\` property in your ImageComponent.js.
        Received: ${currentSrc}
        Expected: ${getCacheKey(images)}
      `);
        return;
      }
    }

    return listenImageLoad(
      imageElement,
      onLoad(images, setStatus),
      onError(setStatus)
    );
  }, [dangerouslyDisableLazyLoad, images, ref, status, setStatus]);
};

const useImageStatus = (images, dangerouslyDisableLazyLoad, preset) => {
  let ref, inView;
  const actualRef = useRef();
  /* eslint-disable react-hooks/rules-of-hooks */
  if (!process.env.SERVER && window.IntersectionObserver) {
    const inViewResult = useInView({
      triggerOnce: true,
      rootMargin: "200px 0px",
    });
    // The ref from useInView is just a function, we can't inspect `ref.current`
    // So we're wrapping it in a function to also store the node in a ref that we
    // can use in our own code.
    const inViewRef = inViewResult[0];
    ref = useCallback(
      (node) => {
        actualRef.current = node;
        inViewRef(node);
      },
      [actualRef, inViewRef]
    );
    inView = inViewResult[1];
  } else {
    ref = actualRef;
    inView = true;
  }
  /* eslint-enable react-hooks/rules-of-hooks */

  const initialState = useMemo(() => {
    return getInitialState(images, preset, dangerouslyDisableLazyLoad);
  }, [images, preset, dangerouslyDisableLazyLoad]);

  const [state, setStatus] = useState(initialState);

  useResetStateForNewImagesProps(
    images,
    dangerouslyDisableLazyLoad,
    initialState,
    setStatus
  );

  useEffect(() => {
    if (state.status === STATUS_LAZY && inView) {
      setStatus((state) => {
        return { ...state, status: STATUS_LOADING };
      });
    }
  }, [inView, state.status]);

  useImageLoad(
    images,
    dangerouslyDisableLazyLoad,
    actualRef,
    state.status,
    setStatus
  );

  return {
    imageRef: ref,
    status: state.status,
    size: state.size,
  };
};

export default useImageStatus;

export { resetImageCache };
